import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useClientMounted } from '../../hooks/useClientMounted';
import PublicSEO from '../../components/public/PublicSEO';
import { parseMedicoSlugToId, getMedicoSlug, getTitoloMedico, formatSlotTime, formatPrestazioneNome } from '../../utils/slug';
import { getSnapApiData } from '../../utils/snapStore';
import { saveMedicoBookingIntent } from '../../utils/bookingIntent';
import { publicPath } from '../../utils/canonicalUrl';
import { buildPhysician, buildBreadcrumb } from '../../utils/jsonLd';
import MedicoPhoto from '../../components/MedicoPhoto';
import { ServiceOption, DayPicker, SlotButton, BookingSummaryBar } from '../../components/ui';
import { formatPrice } from '../../utils/format';
import './MedicoDetailPage.css';

const MedicoDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const id = parseMedicoSlugToId(slug);
  const [medico, setMedico] = useState(() => {
    const cached = id != null ? getSnapApiData(`/public/medici/${id}`) : null;
    if (cached && cached.status === false) return null;
    return cached ?? null;
  });
  const [disponibilita, setDisponibilita] = useState([]);
  const [dateConSlots, setDateConSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(() => {
    if (id == null) return true;
    const cached = getSnapApiData(`/public/medici/${id}`);
    if (cached && cached.status === false) return true;
    return !cached;
  });
  const [disponibilitaLoading, setDisponibilitaLoading] = useState(false);
  const [tariffe, setTariffe] = useState([]);
  const [tariffeLoading, setTariffeLoading] = useState(false);
  const [selectedPrestazione, setSelectedPrestazione] = useState(null);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();
  const clientMounted = useClientMounted();
  const viewerIsAuthenticated = clientMounted && isAuthenticated();

  const loadMedico = async () => {
    if (id == null) {
      if (slug && /^[a-z0-9-]+$/.test(slug) && !/^\d+/.test(slug)) {
        navigate(`/discipline/${slug}`, { replace: true });
        return;
      }
      setError('URL non valido');
      setLoading(false);
      return;
    }
    if (!medico) {
      const cached = id != null ? getSnapApiData(`/public/medici/${id}`) : null;
      if (!cached) setLoading(true);
    }
    setError(null);
    try {
      const response = await api.get(`/public/medici/${id}`);
      setMedico(response.data);
    } catch (err) {
      console.error('Errore caricamento medico:', err);
      const status = err.response?.status;
      if (status === 410) {
        navigate('/410', { replace: true });
        return;
      }
      const is404 = status === 404;
      setError(is404 ? null : (err.response?.data?.message || 'Errore di caricamento. Riprova.'));
      if (is404) setMedico(null);
    } finally {
      setLoading(false);
    }
  };

  const loadProssimiGiorniConDisponibilita = async (prestazione) => {
    if (!medico?.id || !prestazione) {
      setDateConSlots([]);
      setSelectedDate(null);
      setSelectedSlot(null);
      return;
    }
    const durata = prestazione.durata > 0 ? prestazione.durata : 30;
    const videovisita = prestazione.isVideo === true;
    setDisponibilitaLoading(true);
    try {
      const res = await api.get(
        `/public/medici/${medico.id}/disponibilita-prossimi-giorni?limite=7&videovisita=${videovisita}&durata=${durata}`
      );
      const dates = res.data?.dates || [];
      setDateConSlots(dates);
      setSelectedSlot(null);
      if (dates.length > 0) {
        setSelectedDate(dates[0].data);
      } else {
        setSelectedDate(null);
      }
    } catch {
      setDateConSlots([]);
      setSelectedDate(null);
    } finally {
      setDisponibilitaLoading(false);
    }
  };

  useEffect(() => {
    loadMedico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (medico?.id && selectedPrestazione) {
      loadProssimiGiorniConDisponibilita(selectedPrestazione);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medico?.id, selectedPrestazione?.idTariffa]);

  const loadTariffe = async () => {
    if (!medico?.id) return;
    setTariffeLoading(true);
    try {
      const res = await api.get(`/public/medici/${medico.id}/tariffe`);
      setTariffe(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTariffe([]);
    } finally {
      setTariffeLoading(false);
    }
  };

  useEffect(() => {
    if (!medico?.id) return;
    setSelectedPrestazione(null);
    loadTariffe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medico?.id]);

  useEffect(() => {
    if (tariffe.length > 0 && !selectedPrestazione) {
      setSelectedPrestazione(tariffe[0]);
    }
  }, [tariffe, selectedPrestazione]);

  useEffect(() => {
    if (selectedDate && dateConSlots.length > 0) {
      const found = dateConSlots.find(d => d.data === selectedDate);
      setDisponibilita(found ? (found.slots || []) : []);
    }
    setSelectedSlot(null);
  }, [selectedDate, dateConSlots]);

  const goToBooking = (slot) => {
    if (!medico || !selectedDate || !slot || !selectedPrestazione) return;
    const canonicalSlug = getMedicoSlug(medico);
    const pagatore = searchParams.get('pagatore') || 'PRIVATO';
    const idFondoRaw = searchParams.get('idFondo');
    const intent = saveMedicoBookingIntent(medico, canonicalSlug, selectedDate, slot, {
      idTariffa: selectedPrestazione.idTariffa,
      durataMin: selectedPrestazione.durata > 0 ? selectedPrestazione.durata : 30,
      isVideo: selectedPrestazione.isVideo === true,
      nomePrestazione: selectedPrestazione.nomePrestazione,
      importo: selectedPrestazione.importo,
      pagatore,
      idFondo: idFondoRaw ? Number(idFondoRaw) : null,
      modalita: selectedPrestazione.isVideo ? 'VIDEO' : 'STUDIO',
      canale: 'PUBLIC_PROFILE',
    });
    if (!intent) return;
    if (viewerIsAuthenticated) {
      navigate(intent.path, { state: intent.state });
      return;
    }
    navigate('/login', {
      state: {
        from: publicPath(`/medici/${canonicalSlug}`),
        afterLogin: intent,
      },
    });
  };

  const handleSlotClick = (slot) => {
    if (!slot.disponibile) return;
    setSelectedSlot((prev) =>
      (prev && prev.oraInizio === slot.oraInizio && prev.oraFine === slot.oraFine) ? null : slot);
  };

  const handlePrenotaClick = () => {
    if (!selectedSlot || !selectedDate) return;
    goToBooking(selectedSlot);
  };

  if (id == null) {
    return (
      <div className="medico-detail-page">
        <PublicSEO title="URL non valido" canonicalPath="/medici" noIndex />
        <div className="container medico-detail-container">
          <p>URL non valido.</p>
          <Link to={publicPath('/medici')} className="vc-btn vc-btn-primary">Torna all&apos;elenco medici</Link>
        </div>
      </div>
    );
  }

  if (loading && !medico && !error) {
    return (
      <div className="medico-detail-page">
        <PublicSEO title="Medico" canonicalPath={`/medici/${slug}`} noIndex />
        <div className="container medico-detail-container medico-detail-loading-shell" aria-busy="true" aria-label="Caricamento" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="medico-detail-page">
        <PublicSEO title="Errore" canonicalPath={`/medici/${slug}`} noIndex />
        <div className="container medico-detail-container">
          <div className="medico-detail-error">
            <p>{error}</p>
            <button type="button" className="medico-detail-retry-btn" onClick={loadMedico}>Riprova</button>
            <Link to={publicPath('/medici')} className="medico-detail-back-link">Torna all&apos;elenco medici</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!medico) {
    return (
      <div className="medico-detail-page">
        <PublicSEO title="Medico non trovato" canonicalPath="/medici" noIndex />
        <div className="container medico-detail-container">
          <p>Medico non trovato.</p>
          <Link to={publicPath('/medici')} className="vc-btn vc-btn-primary">Torna all&apos;elenco medici</Link>
        </div>
      </div>
    );
  }

  const nome = medico.nome ?? medico.firstName ?? '';
  const cognome = medico.cognome ?? medico.lastName ?? '';
  const nomeCompleto = [nome, cognome].filter(Boolean).join(' ') || 'Medico';
  const titolo = getTitoloMedico(medico);
  const branca = medico.branca ?? '';
  const specializzazione = medico.specializzazione ?? '';
  const specializzazioni = medico.specializzazioni != null
    ? (Array.isArray(medico.specializzazioni) ? medico.specializzazioni.join(', ') : String(medico.specializzazioni))
    : '';
  const spec = specializzazione || medico.branca || 'Specialista';
  const canonicalSlug = getMedicoSlug(medico);
  const title = `${titolo} ${nomeCompleto} - ${spec} | Visita online`;
  const description = `Prenota una visita medica online con ${titolo} ${nomeCompleto}, ${spec}. Videoconsulto e consulenza su Virtual Clinic.`;
  const primaDataDisponibile = dateConSlots.length > 0 && dateConSlots[0].data
    ? new Date(dateConSlots[0].data + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })
    : null;

  // Redirect allo slug canonico SEO se l'URL era solo l'id (es. /medici/1 → /medici/1-mario-rossi)
  if (slug !== canonicalSlug && !location.state?.skipRedirect) {
    navigate(publicPath(`/medici/${canonicalSlug}`), { replace: true, state: { skipRedirect: true } });
    return null;
  }

  const jsonLd = [
    buildPhysician(medico, {
      canonicalPath: `/medici/${canonicalSlug}`,
      titolo,
      nomeCompleto,
      specialty: spec,
    }),
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Medici', path: '/medici' },
      { name: `${titolo} ${nomeCompleto}` },
    ]),
  ];

  return (
    <div className="medico-detail-page">
      <PublicSEO
        title={title}
        description={description}
        canonicalPath={`/medici/${canonicalSlug}`}
        ogImage={medico.urlImg}
        ogImageAlt={`Foto di ${titolo} ${nomeCompleto}`}
        ogType="profile"
        keywords={`${spec}, ${nomeCompleto}, telemedicina, visita medica online, videoconsulto`}
        jsonLd={jsonLd}
      />
      <div className="container medico-detail-container">
        <nav className="medico-detail-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="medico-detail-breadcrumb-sep" aria-hidden>/</span>
          <Link to={publicPath('/medici')}>Medici</Link>
          <span className="medico-detail-breadcrumb-sep" aria-hidden>/</span>
          <span className="medico-detail-breadcrumb-current">{titolo} {nomeCompleto}</span>
        </nav>

        <article className="medico-detail-card">
          <div className="medico-detail-header">
            <MedicoPhoto
              src={medico.urlImg}
              alt={`${titolo} ${nomeCompleto}`}
              variant="detail"
              className="medico-detail-photo"
            />
            <div className="medico-detail-info">
              <h1 className="medico-detail-title">
                <span className="medico-detail-titolo">{titolo}</span>{' '}
                <span className="medico-detail-nome-cognome">{nomeCompleto}</span>
              </h1>
              {(branca || specializzazione || specializzazioni) && (
                <div className="medico-detail-spec-wrap">
                  {branca && <p className="medico-detail-spec">{branca}</p>}
                  {(specializzazione || specializzazioni) && (specializzazione !== branca || specializzazioni) && (
                    <p className="medico-detail-spec">{specializzazione || specializzazioni}</p>
                  )}
                </div>
              )}
              {primaDataDisponibile && (
                <p className="medico-detail-prima-disponibilita">
                  Prima disponibilità: {primaDataDisponibile}
                </p>
              )}
            </div>
          </div>

          <div className="medico-detail-layout">
          <div className="medico-detail-main">
              {medico.bio && (
                <div
                  className="medico-detail-bio medico-detail-bio-html"
                  dangerouslySetInnerHTML={{ __html: medico.bio }}
                />
              )}
          </div>
          <aside className="medico-detail-book" aria-label="Prenota">

          {tariffeLoading ? (
            <p className="medico-detail-no-slots">Caricamento prestazioni...</p>
          ) : tariffe.length > 0 ? (
            <section className="medico-detail-prestazioni" aria-labelledby="prestazioni-heading">
              <h2 id="prestazioni-heading" className="medico-detail-section-title">Prestazione</h2>
              <div className="d-flex flex-column gap-2">
                {tariffe.map((t) => (
                  <ServiceOption
                    key={t.idTariffa}
                    name={formatPrestazioneNome(t.nomePrestazione)}
                    durationMin={t.durata > 0 ? t.durata : 30}
                    mode={t.isVideo ? 'video' : 'studio'}
                    price={t.importo}
                    selected={selectedPrestazione?.idTariffa === t.idTariffa}
                    onSelect={() => setSelectedPrestazione(t)}
                  />
                ))}
              </div>
            </section>
          ) : null}

          <section className="medico-detail-disponibilita" aria-labelledby="disponibilita-heading">
            <h2 id="disponibilita-heading" className="medico-detail-section-title">Giorno e orario</h2>
            {!selectedPrestazione ? (
              <p className="medico-detail-no-disponibilita">Seleziona una prestazione per vedere le disponibilità.</p>
            ) : disponibilitaLoading ? (
              <p className="medico-detail-no-slots">Caricamento disponibilità...</p>
            ) : dateConSlots.length === 0 ? (
              <p className="medico-detail-no-disponibilita">Nessuna disponibilità per questa prestazione al momento.</p>
            ) : (
              <>
                <DayPicker
                  days={dateConSlots.map((d) => ({
                    date: d.data,
                    count: Array.isArray(d.slots) ? d.slots.length : undefined,
                  }))}
                  selectedDate={selectedDate}
                  onSelect={setSelectedDate}
                />
                <div className="medico-detail-slots d-flex flex-wrap gap-2 mt-3">
                  {disponibilita.length === 0 ? (
                    <p className="medico-detail-no-slots">Nessuno slot disponibile in questa data.</p>
                  ) : (
                    disponibilita.map((slot, index) => {
                      const isSelected = selectedSlot
                        && selectedSlot.oraInizio === slot.oraInizio
                        && selectedSlot.oraFine === slot.oraFine;
                      return (
                        <SlotButton
                          key={index}
                          time={formatSlotTime(slot.oraInizio)}
                          selected={!!isSelected}
                          disabled={!slot.disponibile}
                          onClick={() => handleSlotClick(slot)}
                        />
                      );
                    })
                  )}
                </div>
              </>
            )}
          </section>

          {(dateConSlots.length > 0 || viewerIsAuthenticated) && (
            <div className="medico-detail-card-cta">
              {selectedSlot ? (
                <>
                  <button
                    type="button"
                    className="vc-btn vc-btn-primary medico-detail-prenota-btn d-none d-md-inline-flex"
                    onClick={handlePrenotaClick}
                  >
                    Prenota visita
                    {selectedPrestazione?.importo != null ? ` · ${formatPrice(selectedPrestazione.importo)}` : ''}
                  </button>
                  <div className="d-md-none" style={{ height: 72 }} aria-hidden="true" />
                  <BookingSummaryBar
                    summary={[
                      formatPrestazioneNome(selectedPrestazione?.nomePrestazione),
                      selectedDate,
                      selectedSlot ? formatSlotTime(selectedSlot.oraInizio) : '',
                    ].filter(Boolean).join(' · ')}
                    total={selectedPrestazione?.importo}
                    ctaLabel="Continua"
                    onCta={handlePrenotaClick}
                  />
                </>
              ) : viewerIsAuthenticated ? (
                <>
                  <p className="medico-detail-cta-hint">Seleziona data e orario sopra per prenotare con lo slot scelto.</p>
                  <button
                    type="button"
                    className="vc-btn vc-btn-primary medico-detail-prenota-btn"
                    onClick={() => navigate(`/app/paziente/prenota/medico/${canonicalSlug}`)}
                  >
                    Prenota
                  </button>
                </>
              ) : (
                <p className="medico-detail-cta-hint">
                  Scegli prestazione, giorno e orario. Il pulsante Prenota resta in questo pannello.
                </p>
              )}
            </div>
          )}
          </aside>
          </div>
        </article>

        <nav className="medico-detail-back" aria-label="Navigazione">
          <Link to={publicPath('/medici')} className="medico-detail-back-link">
            ← Torna all&apos;elenco medici
          </Link>
        </nav>
      </div>
    </div>
  );
};

export default MedicoDetailPage;
